import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Form, FormikProvider, useFormik } from "formik";
import { FC } from "react";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useTransferBillingAndRemoveClientMutation } from "@api/organizationClient.api";
import { Organization } from "@common/types/organization";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import styles from "../ClientRemoval.module.scss";
import {
    transferBillingFormSchema,
    TransferBillingFormSchema,
} from "../../useTransferBillingOptions";
import { ClientDetailsRow } from "../../views/clientViewConfig";

interface Props {
    client: ClientDetailsRow;
    onClose: () => void;
}

export const TransferBillingAndRemoveClientModal: FC<Props> = ({
    client,
    onClose,
}) => {
    const { toast } = useToaster();
    const newPrimaryAdminOptions =
        client.members
            ?.filter((member) => member.organizationId === null)
            .map((member) => ({
                value: member.userId,
                label: member.preferredName ?? "",
            })) ?? [];

    const activeOrganization = useActiveOrganization() as Organization;
    const transferBillingAndRemoveClientMutation =
        useTransferBillingAndRemoveClientMutation();

    const formik = useFormik<TransferBillingFormSchema>({
        initialValues: {
            newPrimaryAdminId:
                newPrimaryAdminOptions.length > 0
                    ? newPrimaryAdminOptions[0].value
                    : 0,
        },
        validate: validateFormikWithZodSchema(transferBillingFormSchema),
        onSubmit: submitHelper({
            handler: async (values) => {
                await transferBillingAndRemoveClientMutation.mutateAsync({
                    clientId: client.id,
                    organizationId: activeOrganization.id,
                    newPrimaryAdminId: values.newPrimaryAdminId,
                });
                toast("Client removed");
                onClose();
            },
        }),
    });

    return (
        <>
            <StandardModalHeader>Remove client?</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <Form>
                        <section>
                            <p>This action cannot be undone.</p>
                            <ul>
                                <li>
                                    You'll lose access to their workspace and
                                    books
                                </li>
                                <li>They'll be downgraded to the Free plan</li>
                                <li>
                                    Their data will remain accessible to them
                                    unless removed
                                </li>
                            </ul>

                            <p>
                                Before the client is removed, billing ownership
                                will be transferred to the user selected below:
                            </p>

                            <FormikField<number>
                                name="newPrimaryAdminId"
                                label="Billing owner"
                                control="button"
                                className={styles.buttonField}
                            >
                                <Picker.Select
                                    items={newPrimaryAdminOptions}
                                    value={
                                        newPrimaryAdminOptions.some(
                                            (option) =>
                                                option.value ===
                                                formik.values.newPrimaryAdminId,
                                        )
                                            ? formik.values.newPrimaryAdminId
                                            : null
                                    }
                                    onValueChange={(v) => {
                                        if (v !== null) {
                                            formik.setFieldValue(
                                                "newPrimaryAdminId",
                                                v,
                                            );
                                        }
                                    }}
                                    disabled={
                                        newPrimaryAdminOptions.length <= 1
                                    }
                                    placeholder="Select admin"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                            block
                                        />
                                    }
                                />
                            </FormikField>
                        </section>

                        <footer className={styles.footer}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                                disabled={formik.isSubmitting}
                                size="xl"
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                type="submit"
                                variant="primary"
                                disabled={
                                    !formik.isValid || formik.isSubmitting
                                }
                                loading={formik.isSubmitting}
                                size="xl"
                            >
                                Yes, remove
                            </ButtonWithLoader>
                        </footer>
                    </Form>
                </FormikProvider>
            </StandardModalBody>
        </>
    );
};
